/* @flow */
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { deviceFromGens } from '../../util/deviceFromGens'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import * as actionCreators from './index'
import * as deviceSchema from './schemas'
import changeCase from 'change-object-case'

// types
import type { Address, GeoPoint } from '../location/types'
import type { Dispatch } from '../../util/redux'

/**
 * A module that exports `create`, `get`, and `update` functions for devices
 * @module
 */

const Api = new API()

/**
 * Get device
 * @param {string} userAuthToken - User's authorization token
 * @param {string} deviceId - Device id
 * @returns {object} An action related to the device retrieval process
 */
export function get(userAuthToken: string, deviceId: string) {
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'GET',
      headers: createDefaultHeaders(userAuthToken)
    }

    dispatch(actionCreators.getDeviceInit({ deviceId }))

    return Api.call(`device/getDevice/${deviceId}`, reqObj)
      .then(response => response.json())
      .then(json => dispatch(actionCreators.getDeviceComplete(json)))
      .catch(err => dispatch(actionCreators.getDeviceComplete(err)))
  }
}

// Update /////////////////////////////////////////////////////////////////////

type UpdateData = {|
  id: string,
  name?: string,
  address?: Address,
  geoPoint?: GeoPoint,
  settleTime?: number,
  standby?: boolean
|}

/**
 * Update device
 * @param {string} userAuthToken - User's auth token
 * @param {object} updateData - Device data to update
 * @returns {promise} - UpdateComplete action
 */
export const update = (userAuthToken: string, updateData: UpdateData) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(updateData, { recursive: true })
    const error = isInvalid(
      deviceSchema.UpdateIrrigationControllerRequest,
      reqData
    )
    const reqObj = {
      method: 'PUT',
      body: JSON.stringify(reqData),
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + userAuthToken
      }
    }

    dispatch(actionCreators.updateInit(updateData))

    if (error) {
      return Promise.reject(dispatch(actionCreators.updateComplete(error)))
    }

    return Api.call('device/updateIrrigationController', reqObj)
      .then(response => response.json())
      .then(json => {
        const device = deviceFromGens(json)
        dispatch(actionCreators.set(device))
        return dispatch(actionCreators.updateComplete(json))
      })
      .catch(err => dispatch(actionCreators.updateComplete(err)))
  }
}

/**
 * Delete device
 * @param {string} userAuthToken - User's auth token
 * @param {string} id            - Device ID
 * @return {promise} - deleteDeviceComplete action
 */
export const del = (userAuthToken: string, id: string) => {
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'DELETE',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      }
    }

    dispatch(actionCreators.deleteDeviceInit({ id }))

    return Api.call('device/deleteDevice/' + id, reqObj)
      .then(res => res.json())
      .then(json => {
        dispatch(actionCreators.remove(id))
        return dispatch(actionCreators.deleteDeviceComplete(json))
      })
      .catch(err => {
        return dispatch(actionCreators.deleteDeviceComplete(err))
      })
  }
}
